import { Button, TypographyProps } from "@mui/material";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DamageCostEvaluationDto,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleOperationType,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import DamageCostEvaluationMenu from "../DamageCostEvaluationMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import ContractTabContent from "./Tabs/ContractTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import InboxTabContent from "./Tabs/InboxTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import RelatedOperationsTabContent from "./Tabs/RelatedOperationsTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum DamageCostEvaluationViewPageTabs {
  Overview = "Overview",
  Inbox = "Inbox",
  Vehicle = "Vehicle",
  Contract = "Contract",
  RelatedOperations = "RelatedOperations",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

export interface DamageCostEvaluationViewProps extends BaseEntityViewInheritableProps<DamageCostEvaluationDto> {
  damageCostEvaluationId: string | null | undefined;
  damageCostEvaluation: DamageCostEvaluationDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function DamageCostEvaluationView({
  damageCostEvaluationId,
  damageCostEvaluation,
  displayProps = defaultDisplayProps,
  headerProps = defaultHeaderProps,
  onUpdate,
  onDelete,
  onClose,
}: DamageCostEvaluationViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  headerProps = {
    ...defaultHeaderProps,
    ...headerProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.DamageCostEvaluation),
      viewVariant: displayProps.viewVariant,
    },
  });

  const damageCostEvaluationRequest = useApiRequest(
    apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      damageCostEvaluationId: damageCostEvaluationId!,
    },
    {
      debugKey: "DamageCostEvaluationView",
      deps: [damageCostEvaluationId],
      skip: !damageCostEvaluationId || !!damageCostEvaluation,
    },
  );
  damageCostEvaluation = damageCostEvaluation || damageCostEvaluationRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.DamageCostEvaluation,
        damageCostEvaluation?.id || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      damageCostEvaluationRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps?.breadcrumbs &&
        damageCostEvaluation && {
          idValue: damageCostEvaluation.id!,
          newTitle: damageCostEvaluation.localNumber || "",
        }) ||
      undefined,
  });

  return (
    <BaseEntityView
      entityType={EntityType.DamageCostEvaluation}
      entityId={damageCostEvaluationId}
      entity={damageCostEvaluation}
      entityRequest={damageCostEvaluationRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          displayProps.header &&
          damageCostEvaluation && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.DamageCostEvaluation}
              operationEntity={damageCostEvaluation}
              vehicleId={damageCostEvaluation?.vehicle?.id}
              vehicle={damageCostEvaluation?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.DAMAGE_COST_EVALUATION_VIEW(damageCostEvaluation?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps.actions && (
                  <AuthorizedTenantElement tenantIds={[damageCostEvaluation?.tenantId]}>
                    <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationManage]}>
                      <Button
                        component={AppLink}
                        to={ROUTE_PATH.DAMAGE_COST_EVALUATION_EDIT(damageCostEvaluation?.id)}
                        variant='outlined'
                        color='text'
                        size='medium'
                        startIcon={<AppIcon of='edit' />}
                      >
                        Edit
                      </Button>
                    </AuthorizedElement>
                  </AuthorizedTenantElement>
                )
              }
              secondaryActions={
                <DamageCostEvaluationMenu
                  entity={damageCostEvaluation}
                  displayProps={{
                    actions: {
                      downloadPdf: true,
                      sendToCustomer: true,
                      edit: false,
                      addToContract: true,
                      duplicateAsDraft: true,
                      delete: true,
                      createRepairOperation: true,
                      addToAssignment: true,
                    },
                  }}
                  onUpdate={(newValue) => {
                    newValue
                      ? damageCostEvaluationRequest.replaceData(newValue)
                      : damageCostEvaluationRequest.refetch();
                    onUpdate?.(newValue);
                  }}
                  onDelete={() => (onDelete ? onDelete() : history.goBack())}
                />
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                damageCostEvaluationRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={DamageCostEvaluationViewPageTabs}
          defaultTabId={DamageCostEvaluationViewPageTabs.Overview}
          viewVariant={displayProps.viewVariant}
          commonViewParams={commonViewParams}
          tabs={[
            {
              label: "Overview",
              value: DamageCostEvaluationViewPageTabs.Overview,
              isHideable: false,
            },
            { label: "Chats", value: DamageCostEvaluationViewPageTabs.Inbox },
            {
              label: "Vehicle",
              value: DamageCostEvaluationViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            ...(damageCostEvaluation?.contract?.id
              ? [
                  {
                    label: "Contract",
                    value: DamageCostEvaluationViewPageTabs.Contract,
                    permissions: [AppPermission.ContractRead],
                  },
                ]
              : []),
            {
              label: "Related operations",
              value: DamageCostEvaluationViewPageTabs.RelatedOperations,
            },
            {
              label: "History",
              value: DamageCostEvaluationViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: DamageCostEvaluationViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            damageCostEvaluation && (
              <>
                {activeTab === DamageCostEvaluationViewPageTabs.Overview && (
                  <OverviewTabContent
                    damageCostEvaluation={damageCostEvaluation}
                    onUpdate={(newValue) => {
                      newValue
                        ? damageCostEvaluationRequest.replaceData(newValue)
                        : damageCostEvaluationRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                  />
                )}
                {activeTab === DamageCostEvaluationViewPageTabs.Inbox && (
                  <InboxTabContent damageCostEvaluation={damageCostEvaluation} />
                )}
                {activeTab === DamageCostEvaluationViewPageTabs.Vehicle && (
                  <VehicleTabContent damageCostEvaluation={damageCostEvaluation} />
                )}
                {activeTab === DamageCostEvaluationViewPageTabs.Contract && (
                  <ContractTabContent damageCostEvaluation={damageCostEvaluation} />
                )}
                {activeTab === DamageCostEvaluationViewPageTabs.RelatedOperations && (
                  <RelatedOperationsTabContent damageCostEvaluation={damageCostEvaluation} />
                )}
                {activeTab === DamageCostEvaluationViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent damageCostEvaluation={damageCostEvaluation} />
                )}
                {activeTab === DamageCostEvaluationViewPageTabs.Comments && (
                  <CommentsTabContent damageCostEvaluation={damageCostEvaluation} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
